import { useEffect, useRef, useState } from 'react';
import { SMPContainer, SMPFlex, SMPImage, SMPText } from '@velinsi/smp-widgets-ui';
import { INormalizedTeamSquadGridItem, TeamPlayerStat } from '../../../types';
import { LinkWrapper } from '../../../../../../packages/wrappers/LinkWrapper';
import { SMPGoalIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPGoalIcon';
import { SMPAssistIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPAssistIcon';
import { SMPMinutesPlayedIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPMinutesPlayedIcon';
import { SMPYellowCardIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPYellowCardIcon';
import { SMPRedCardIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPRedCardIcon';
import { SMPPlayedIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPPlayedIcon';
import { SMPAgeIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPAgeIcon';
import { SMPStartedIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPStartedIcon';
import { SMPConcededIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPConcededIcon';
import { SMPCleansheetsIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPCleansheetsIcon';
import { SMPMinutesSubstituteIcon } from '@velinsi/smp-widgets-ui/src/atoms/icons/SMPMinutesSubstituteIcon';
import { SMPTooltip } from '@velinsi/smp-widgets-ui/src/atoms/SMPTooltip';

interface ITeamSquadGridViewProps {
  items: { name: string; data: INormalizedTeamSquadGridItem[] };
  handleClick: (id: string | number, name: string) => void;
  measuredRef?: (node: HTMLDivElement) => void;
}

const getCorrectIcon = (icon: TeamPlayerStat) => {
  const iconCss = { width: '20px', height: '20px' };
  const iconPlayedCss = { width: '31px', height: '20px' };
  const goalsCss = { width: '28px', height: '20px' };
  const startedCss = { width: '24px', height: '20px' };

  switch (icon) {
    case 'playerAge':
      return <SMPAgeIcon css={iconCss} />;
    case 'played':
      return <SMPPlayedIcon css={iconPlayedCss} />;
    case 'minutes':
      return <SMPMinutesPlayedIcon css={startedCss} />;
    case 'yellowCards':
      return <SMPYellowCardIcon css={iconCss} />;
    case 'redCards':
      return <SMPRedCardIcon css={iconCss} />;
    case 'goals':
      return <SMPGoalIcon css={goalsCss} />;
    case 'assists':
      return <SMPAssistIcon css={goalsCss} />;
    case 'started':
      return <SMPStartedIcon css={startedCss} />;
    case 'conceded':
      return <SMPConcededIcon css={iconPlayedCss} />;
    case 'cleansheets':
      return <SMPCleansheetsIcon css={iconPlayedCss} />;
    case 'minutesSubstitute':
      return <SMPMinutesSubstituteIcon css={startedCss} />;
    default:
      return null;
  }
};

const toggleTooltip = (e: React.PointerEvent<HTMLSpanElement>, show: boolean) => {
  if (e.target instanceof Element === false) {
    return;
  }

  const statContainer: HTMLDivElement | null = (e.target as Element).closest('.stat-container');
  const tooltip: HTMLDivElement | null | undefined = statContainer?.querySelector('.stat-tooltip');

  if (tooltip) {
    try {
      tooltip.style.display = show ? 'block' : 'none';
    } catch (e) {
      console.error(e);
    }
  }
};

function TeamSquadGridView({ items, handleClick, measuredRef }: ITeamSquadGridViewProps): JSX.Element {
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    const resizeObserver = new ResizeObserver(() => handleResize());
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    handleResize(); // Set initial width

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  const isSmallScreen = containerWidth !== null && containerWidth < 360;

  return (
    <div ref={containerRef}>
      <SMPContainer
        variant='paddinglessContainer'
        css={{
          mb: '$1',
        }}
        ref={measuredRef}
      >
        {items?.name && (
          <SMPFlex variant='gridGroupContrast' css={{ marginTop: '$3', padding: '10px 16px', alignItems: 'flex-start' }}>
            <SMPText
              css={{
                size: '16px',
                fontWeight: '700',
              }}
              variant='contrast'
              size='3'
              weight='700'
            >
              {items?.name}
            </SMPText>
          </SMPFlex>
        )}
        <SMPContainer
          style={{ display: 'grid', gridGap: '16px', alignItems: 'flex-start', marginTop: '10px' }}
          css={{
            gridTemplateColumns: isSmallScreen ? '1fr' : 'repeat(auto-fill, minmax(336px, 1fr))',
            justifyContent: 'center',
          }}
        >
          {items?.data?.map(({ player }) => {
            return (
              <div key={player.id}>
                <LinkWrapper
                  key={player.id}
                  target={player.urlTarget}
                  url={player?.link}
                  onClick={() => handleClick && handleClick(player.id, player.name)}
                  isTeamSquad={true}
                >
                  <SMPContainer
                    css={{
                      bc: '$rowBackgroundColor',
                      width: '100%',
                      borderTopLeftRadius: '$2',
                      borderTopRightRadius: '$2',
                      padding: '8px',
                      backgroundColor: '$primaryBackgroundColor',
                      position: 'relative',
                    }}
                  >
                    <SMPFlex css={{ justifyContent: 'normal', alignItems: 'center' }}>
                      <div style={{ position: 'relative' }}>
                        <SMPImage
                          variant='livescoreEntityLogo'
                          src={player.playerImage}
                          alt={`${player.name} image`}
                          css={{ width: '42px', height: '42px', borderRadius: '$round' }}
                        />
                      </div>
                      <SMPFlex style={{ flexDirection: 'column' }} css={{ marginLeft: '$2' }}>
                        <SMPText variant='contrast' size='4' onClick={() => handleClick && handleClick(player.id, player.name)}>
                          {player.name}
                        </SMPText>

                        <SMPFlex
                          css={{
                            alignItems: 'center',
                            justifyContent: 'normal',
                            marginTop: '$2',
                          }}
                        >
                          <SMPImage
                            variant='livescoreEntityLogo'
                            src={player.countryFlag}
                            alt={`${player.countryName} Logo`}
                            css={{ width: '16px', height: '16px', borderRadius: '$round' }}
                          />
                          <SMPText variant='contrast' size='3' css={{ marginLeft: '$2' }}>
                            {player.countryName}
                          </SMPText>
                        </SMPFlex>
                      </SMPFlex>
                    </SMPFlex>
                    <SMPText
                      css={{
                        size: '14px',
                        fontWeight: '700',
                        lineHeight: '19px',
                        color: '$diffContrastBgColor',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'absolute',
                        right: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                      variant='diffContrast'
                      size='3'
                    >
                      {player.shirtNumber}
                    </SMPText>
                  </SMPContainer>
                </LinkWrapper>
                <SMPContainer
                  css={{
                    bc: '$rowBackgroundColor',
                    borderBottomLeftRadius: '$2',
                    borderBottomRightRadius: '$2',
                    px: '$5',
                    py: '$3',
                    backgroundColor: '$pillBackgroundColor',
                    display: 'grid',
                  }}
                >
                  <SMPFlex
                    style={{
                      gridTemplateColumns: isSmallScreen ? 'repeat(5, 1fr)' : 'repeat(6, 1fr)',
                    }}
                    css={{
                      justifyContent: 'center',
                      gap: '16px',
                      display: 'grid',
                      gridAutoRows: 'minmax(0, auto)',
                      alignItems: 'center',
                      textAlign: 'center',
                      padding: '16px 8px',
                    }}
                  >
                    {player.stats.map((stat) => {
                      return (
                        <SMPFlex
                          className='stat-container'
                          direction={'column'}
                          key={stat.name}
                          css={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            userSelect: 'none',
                          }}
                          onPointerEnter={(e) => toggleTooltip(e, true)}
                          onPointerLeave={(e) => toggleTooltip(e, false)}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onContextMenu={
                            (e) => e.preventDefault() // This will prevent the context menu from showing on long press
                          }
                        >
                          <div style={{ position: 'relative' }}>
                            <SMPTooltip
                              className='stat-tooltip'
                              css={{
                                bottom: '100%',
                                marginBottom: '10px',
                              }}
                              style={{ left: '36%' }}
                            >
                              <span>{stat.tooltip}</span>
                            </SMPTooltip>
                            {getCorrectIcon(stat.name)}
                          </div>
                          <SMPText
                            variant='contrast'
                            size='3'
                            css={{
                              marginTop: '$2',
                              position: 'relative',
                              right: '4px',
                              color: '$playerStatisticsColor',
                            }}
                          >
                            {stat.value}
                          </SMPText>
                        </SMPFlex>
                      );
                    })}
                  </SMPFlex>
                </SMPContainer>
              </div>
            );
          })}
        </SMPContainer>
      </SMPContainer>
    </div>
  );
}

export default TeamSquadGridView;
